import React from 'react';

export const toPathSegments = (path: string): string[] =>
    path.replace(/\[(\d+)\]/g, '.$1').split('.').filter(Boolean);

export const setNestedValue = (target: Record<string, any>, path: string, value: unknown) => {
    const segments = toPathSegments(path);
    if (!segments.length) return;
    let current: any = target;
    segments.forEach((segment, index) => {
        const isLast = index === segments.length - 1;
        if (isLast) {
            current[segment] = value;
            return;
        }
        if (current[segment] === undefined || current[segment] === null || typeof current[segment] !== 'object') {
            const nextSegment = segments[index + 1];
            current[segment] = /^\d+$/.test(nextSegment) ? [] : {};
        }
        current = current[segment];
    });
};

const extractErrorMessage = (value: unknown): string | undefined => {
    if (typeof value === 'string') {
        return value;
    }
    if (Array.isArray(value)) {
        for (const item of value) {
            const message = extractErrorMessage(item);
            if (message) {
                return message;
            }
        }
        return undefined;
    }
    if (value && typeof value === 'object' && 'message' in value) {
        const message = (value as {message?: unknown}).message;
        return typeof message === 'string' ? message : undefined;
    }
    return undefined;
};

/**
 * Drops a field's submit-time error. A schema (form-level) error lands on the field's meta and stays there until that
 * field is validated again, which never happens for values written through `setFieldValue` without a mounted `Field`
 * (the formats table, the multi-format flag): the form would stay invalid and Save would do nothing. The next submit
 * re-runs the schema and puts the error back if the value is still wrong.
 */
export const clearFieldSubmitError = (form: {setFieldMeta?: (field: never, updater: (meta: any) => any) => void}, field: string): void => {
    form.setFieldMeta?.(field as never, (meta: any) => ({
        ...meta,
        errorMap: {...meta?.errorMap, onSubmit: undefined},
        errorSourceMap: {...meta?.errorSourceMap, onSubmit: undefined}
    }));
};

export const getFormBridgeErrors = (form: {getAllErrors?: () => any}): Record<string, any> => {
    const result: Record<string, any> = {};
    const allErrors = form.getAllErrors?.();
    const fields = allErrors?.fields ?? {};

    Object.entries(fields).forEach(([path, field]) => {
        const source = (field as any)?.errorMap?.onSubmit ?? (field as any)?.errors;
        const message = extractErrorMessage(source);
        if (message) {
            setNestedValue(result, path, message);
        }
    });

    return result;
};

interface IBidderFormCtx {
    form: any;
    formBridge: any;
}

export const BidderFormCtx = React.createContext<IBidderFormCtx | null>(null);

export const Field = ({name, component: Component, ...props}: {
    name: string;
    component: React.ComponentType<any>;
    [key: string]: any;
}) => {
    const ctx = React.useContext(BidderFormCtx)!;
    return (
        <ctx.form.Field name={name as never}>
            {(fieldApi: any) => (
                <Component
                    {...props}
                    field={{name, value: fieldApi.state.value}}
                    form={ctx.formBridge}
                    meta={{}}
                />
            )}
        </ctx.form.Field>
    );
};
